import Sentry from "@similarweb/sentry-logger";

import type { INoTouchAddon } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { getSingleDiscountCode } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { useApiQuery } from "app/modules/api-client";
import { ResponseError } from "app/services/fetchService";

export const NO_TOUCH_PURCHASE_QUERY_TAG = "NoTouchAddonsPurchase";

const CUSTOMER_PAYMENT_ERROR_DETAIL = "CustomerPaymentError";

export const isCustomerPaymentError = (error: unknown): boolean =>
    error instanceof ResponseError && error.problem?.detail === CUSTOMER_PAYMENT_ERROR_DETAIL;

interface IUseNoTouchPurchaseQuery {
    selectedAddon?: INoTouchAddon;
    onPurchaseSuccess: () => void;
    onPurchaseError: () => void;
    onPurchasePaymentError: () => void;
}

export const useNoTouchPurchaseQuery = ({
    selectedAddon,
    onPurchaseSuccess,
    onPurchaseError,
    onPurchasePaymentError,
}: IUseNoTouchPurchaseQuery) => {
    const params = {
        templateId: selectedAddon?.productTemplateId,
        quantity: selectedAddon?.quantity,
        countryFilterValue: selectedAddon?.countryFilterData?.countryCodeIso3,
        discountCode: getSingleDiscountCode(selectedAddon),
    };

    return useApiQuery(
        {
            tag: NO_TOUCH_PURCHASE_QUERY_TAG,
            methodName: "notouchAddonsPurchaseCreate",
            options: {
                enabled: !!selectedAddon,
                select: () => {
                    onPurchaseSuccess();
                },
                throwOnError: (e) => {
                    const error = e instanceof Error ? e : new Error(String(e), { cause: e });

                    Sentry.errorTracker({
                        message: "useNoTouchPurchaseQuery",
                        e: error,
                        params,
                    });

                    if (isCustomerPaymentError(e)) {
                        onPurchasePaymentError?.();
                    } else {
                        onPurchaseError?.();
                    }

                    return false;
                },
            },
        },
        params,
    );
};
